import React, { useState } from 'react';
import { X, ArrowRight } from 'lucide-react';

import custom_api_func from './axios_api_func';
import { StarRating, styles } from './surveyShared';

/*
=====================================================================================
CourseExperienceSurvey
=====================================================================================
A single-step modal asking how the learner's experience with a course was
(star rating + free-text feedback). Unlike the onboarding survey, this is
NOT a one-time thing - render it every time a course hits 100% progress.

HOW TO USE
---------------------------------------------------------------------------------
  const [showCourseSurvey, setShowCourseSurvey] = useState(false);

  useEffect(() => {
    if (progress === 100) {
      setShowCourseSurvey(true);
    }
  }, [progress]);

  {showCourseSurvey && (
    <CourseExperienceSurvey
      academyid={academyid}
      csrftoken={csrftoken}
      course={{ id: currentCourse.id, title: currentCourse.title }}
      onClose={() => setShowCourseSurvey(false)}
    />
  )}

EXPECTED BACKEND ENDPOINT
---------------------------------------------------------------------------------
  POST api/submit_course_survey2/{academyid}/{courseId}/   (multipart/form-data)
        fields: rating, feedback
        -> { success: true }
=====================================================================================
*/

const CourseExperienceSurvey = ({ academyid, course, csrftoken, onClose }) => {
  const [isMobile] = useState(window.innerWidth <= 640);

  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const canSubmit = rating > 0;

  const handleSubmit = () => {
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setSubmitError(false);

    const formData = new FormData();
    formData.append('rating', rating);
    formData.append('feedback', feedback);

    custom_api_func
      .post(`api/submit_course_survey2/${academyid}/${course.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': csrftoken,
        },
      })
      .then(() => setHasSubmitted(true))
      .catch(() => {
        // Don't trap the learner - let them close, just flag it quietly.
        setHasSubmitted(true);
        setSubmitError(true);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: isMobile ? '100%' : 520 }}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerEyebrow}>Course Complete 🎉</div>
            <h2 style={styles.headerTitle}>{course.title}</h2>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={20} color="#5c5d70" />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {!hasSubmitted ? (
            <div>
              <h3 style={styles.stepTitle}>How was your experience?</h3>
              <p style={styles.stepSubtitle}>
                Your rating helps us know if this course is hitting the mark.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                <StarRating value={rating} onChange={setRating} />
              </div>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Anything you loved, or anything we should improve? (optional)"
                rows={4}
                style={styles.textarea}
              />
            </div>
          ) : (
            <div>
              <h3 style={styles.stepTitle}>Thanks for the feedback! 🙌</h3>
              {submitError ? (
                <div style={styles.inlineNotice}>
                  We couldn't save your answers just now, but that's on us - no
                  need to redo anything.
                </div>
              ) : (
                <p style={styles.stepSubtitle}>
                  We've logged your rating for {course.title}.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div style={styles.footer}>
          {!hasSubmitted ? (
            <>
              
              <button
                style={{
                  ...styles.primaryBtn,
                  ...(!canSubmit ? styles.primaryBtnDisabled : {}),
                }}
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
              >
                {submitting ? (
                  'Saving...'
                ) : (
                  <>
                    Submit <ArrowRight size={16} />
                  </>
                )}
              </button>
            </>
          ) : (
            <button style={styles.primaryBtn} onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseExperienceSurvey;